import type {
  AuthSuccessResponse,
  ChangePasswordRequest,
  ContentBlockType,
  CourseShape,
  CreateCourseRequest,
  JoinCourseRequest,
  ListCoursesResponse,
  MeResponse,
  ModuleItemShape,
  ModuleItemType,
  ModuleShape,
  OrganizationShape,
  QuestionType,
  SignInRequest,
  SignupRequest,
  UpdateOrganizationRequest,
  UpdateProfileRequest,
} from '@eduborg/shared';

export type AssessmentShape = {
  id: string;
  courseId: string;
  moduleItemId: string;
  title: string;
  description: string | null;
  category: 'assessment' | 'knowledge_check';
  totalPoints: number;
  timeLimitSeconds: number | null;
  attemptsAllowed: number | null;
  scoringPolicy: 'highest' | 'latest' | 'average';
  availableFrom: number | null;
  availableUntil: number | null;
  autoRelease: boolean;
  settingsJson: string | null;
  createdAt: number;
  updatedAt: number;
};

export type OptionShape = {
  id: string;
  questionId: string;
  label: string;
  matchValue: string | null;
  isCorrect: boolean;
  pointsOverride: number | null;
  position: number;
};

export type QuestionShape = {
  id: string;
  assessmentId: string;
  type: QuestionType;
  promptHtml: string;
  points: number;
  autoGradeable: boolean;
  modelAnswer: string | null;
  payloadJson: string | null;
  position: number;
  options: OptionShape[];
  createdAt: number;
  updatedAt: number;
};

export type AssessmentTreeShape = {
  assessment: AssessmentShape;
  questions: QuestionShape[];
};

export type AttemptShape = {
  id: string;
  assessmentId: string;
  profileId: string;
  attemptNumber: number;
  status: 'in_progress' | 'submitted' | 'auto_submitted' | 'graded' | 'released';
  startedAt: number;
  expiresAt: number | null;
  submittedAt: number | null;
  gradedAt: number | null;
  releasedAt: number | null;
  autoScore: number | null;
  score: number | null;
  maxScore: number | null;
  feedbackHtml: string | null;
};

export type SubmitResultShape = {
  attemptId: string;
  status: 'graded' | 'released';
  autoScore: number;
  maxScore: number;
  requiresInstructorReview: boolean;
  perQuestion: Array<{
    questionId: string;
    result: { awarded: number | null; max: number; requiresInstructor: boolean; reason: string };
  }>;
};

export type ContentBlockShape = {
  id: string;
  moduleItemId: string;
  type: ContentBlockType;
  position: number;
  payloadJson: string;
  createdAt: number;
  updatedAt: number;
};

/** A single lesson-snippet citation echoed back from /ai/tutor. */
export type TutorSourceShape = {
  blockId: string;
  moduleItemId: string;
  lessonTitle: string;
  snippet: string;
};

/* ─── Vibe Space (the org's public branded page at /space/:orgSlug) ─── */

export type VibeSpaceResponse = {
  org: {
    slug: string;
    name: string;
    tagline: string | null;
    aboutHtml: string | null;
    accentColor: string | null;
    heroEmoji: string | null;
    logoR2Key: string | null;
    bannerR2Key: string | null;
    type: string;
  };
  courses: Array<{
    id: string;
    slug: string;
    title: string;
    description: string | null;
    joinCode: string;
  }>;
};

export type VibeSpaceSettings = {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  aboutHtml: string | null;
  accentColor: string | null;
  heroEmoji: string | null;
  vibeSpaceVisible: boolean;
};

/** Study Room mode — drives system prompt suffix + counter on the session. */
export type StudyMode = 'hint' | 'walkthrough' | 'answer';

export type StudySessionShape = {
  id: string;
  courseId: string;
  profileId: string;
  focusModuleItemId: string | null;
  startedAt: number;
  lastActivityAt: number;
  endedAt: number | null;
  activeMs: number;
  turnCount: number;
  hintCount: number;
  answerCount: number;
};

export type StudyMessageShape = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  mode: StudyMode | null;
  sources: TutorSourceShape[] | null;
  createdAt: number;
};

/** One row of /courses/:id/study-times — gradebook study-time aggregate. */
export type StudyTimeRowShape = {
  profileId: string;
  studyTimeMs: number;
};

/** A row from the public Template Library at /templates. */
export type TemplateSummary = {
  id: string;
  orgSlug: string;
  orgName: string;
  slug: string;
  title: string;
  description: string | null;
  heroImageR2Key: string | null;
};

/**
 * Tiny typed fetch client. The web app talks to the api Worker through this so
 * fetch shape mistakes are caught at compile time. As routes grow we'll extract
 * per-resource clients (api.courses, api.assessments, etc.) — for Phase 1 the
 * surface is small enough to inline.
 */

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
    public readonly details?: unknown,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
  }
}

type FetchOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  token?: string | null;
};

async function call<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers,
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

  const res = await fetch(`${BASE}${path}`, init);

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* empty / non-JSON body — leave as null */
  }

  if (!res.ok) {
    const errPayload = (payload ?? {}) as { error?: string; message?: string; details?: unknown };
    throw new ApiError(
      res.status,
      errPayload.error ?? `http_${res.status}`,
      errPayload.message,
      errPayload.details,
    );
  }
  return payload as T;
}

export const api = {
  auth: {
    signUp: (body: SignupRequest) =>
      call<AuthSuccessResponse>('/auth/sign-up', { method: 'POST', body }),
    signIn: (body: SignInRequest) =>
      call<AuthSuccessResponse>('/auth/sign-in', { method: 'POST', body }),
    signOut: (token: string | null) =>
      call<{ ok: true }>('/auth/sign-out', { method: 'POST', token }),
    me: (token: string | null) => call<MeResponse>('/auth/me', { token }),
    updateMe: (token: string | null, body: UpdateProfileRequest) =>
      call<MeResponse>('/auth/me', { method: 'PATCH', body, token }),
    changePassword: (token: string | null, body: ChangePasswordRequest) =>
      call<{ ok: true }>('/auth/change-password', { method: 'POST', body, token }),
    /**
     * Right-to-erasure (FERPA / COPPA / GDPR). The server requires
     * the literal `confirmation: 'DELETE'` payload — this method
     * supplies it so callers can't accidentally hit deletion with
     * an empty body. After success the session is revoked server-side;
     * the caller should follow up with `signOut` + a route bounce.
     */
    deleteAccount: (token: string | null) =>
      call<{ ok: true; summary: unknown }>('/auth/delete-account', {
        method: 'POST',
        body: { confirmation: 'DELETE' },
        token,
      }),
  },
  courses: {
    list: (token: string | null) => call<ListCoursesResponse>('/courses', { token }),
    create: (token: string | null, body: CreateCourseRequest) =>
      call<{ course: CourseShape }>('/courses', { method: 'POST', body, token }),
    getBySlug: (token: string | null, orgSlug: string, courseSlug: string) =>
      call<{ course: CourseShape }>(
        `/courses/by-slug/${encodeURIComponent(orgSlug)}/${encodeURIComponent(courseSlug)}`,
        { token },
      ),
    update: (
      token: string | null,
      id: string,
      body: {
        title?: string;
        description?: string | null;
        status?: 'draft' | 'active' | 'archived';
        heroImageR2Key?: string | null;
        publishedAsTemplate?: boolean;
      },
    ) => call<{ course: CourseShape }>(`/courses/${id}`, { method: 'PATCH', body, token }),
    softDelete: (token: string | null, id: string) =>
      call<{ ok: true }>(`/courses/${id}`, { method: 'DELETE', token }),
    join: (token: string | null, body: JoinCourseRequest) =>
      call<{ course: CourseShape }>('/courses/join', { method: 'POST', body, token }),
    /** Per-student aggregate Study Room time. Instructor / staff only. */
    studyTimes: (token: string | null, courseId: string) =>
      call<{ times: StudyTimeRowShape[] }>(`/courses/${courseId}/study-times`, { token }),
  },
  modules: {
    listForCourse: (token: string | null, courseId: string) =>
      call<{ modules: ModuleShape[] }>(`/courses/${courseId}/modules`, { token }),
    create: (
      token: string | null,
      courseId: string,
      body: { parentModuleId?: string | null; title: string; description?: string },
    ) =>
      call<{ module: ModuleShape }>(`/courses/${courseId}/modules`, {
        method: 'POST',
        body,
        token,
      }),
    reorder: (token: string | null, moduleId: string, position: number) =>
      call<{ ok: true }>(`/courses/modules/${moduleId}/reorder`, {
        method: 'POST',
        body: { position },
        token,
      }),
  },
  moduleItems: {
    listForModule: (token: string | null, moduleId: string) =>
      call<{ items: ModuleItemShape[] }>(`/courses/modules/${moduleId}/items`, { token }),
    create: (
      token: string | null,
      moduleId: string,
      body: { type: ModuleItemType; title: string; payloadJson?: string },
    ) =>
      call<{ item: ModuleItemShape }>(`/courses/modules/${moduleId}/items`, {
        method: 'POST',
        body,
        token,
      }),
    reorder: (token: string | null, itemId: string, position: number) =>
      call<{ ok: true }>(`/courses/items/${itemId}/reorder`, {
        method: 'POST',
        body: { position },
        token,
      }),
  },
  orgs: {
    me: (token: string | null) => call<{ organization: OrganizationShape }>('/orgs/me', { token }),
    update: (token: string | null, id: string, body: UpdateOrganizationRequest) =>
      call<{ organization: OrganizationShape }>(`/orgs/${id}`, {
        method: 'PATCH',
        body,
        token,
      }),
  },
  blocks: {
    listForItem: (token: string | null, itemId: string) =>
      call<{ blocks: ContentBlockShape[] }>(`/courses/items/${itemId}/blocks`, { token }),
    create: (
      token: string | null,
      itemId: string,
      body: { type: ContentBlockType; payloadJson: string },
    ) =>
      call<{ block: ContentBlockShape }>(`/courses/items/${itemId}/blocks`, {
        method: 'POST',
        body,
        token,
      }),
    update: (token: string | null, blockId: string, body: { payloadJson: string }) =>
      call<{ block: ContentBlockShape }>(`/courses/blocks/${blockId}`, {
        method: 'PATCH',
        body,
        token,
      }),
    remove: (token: string | null, blockId: string) =>
      call<{ ok: true }>(`/courses/blocks/${blockId}`, { method: 'DELETE', token }),
    reorder: (token: string | null, blockId: string, position: number) =>
      call<{ ok: true }>(`/courses/blocks/${blockId}/reorder`, {
        method: 'POST',
        body: { position },
        token,
      }),
  },
  assessments: {
    create: (
      token: string | null,
      body: {
        courseId: string;
        moduleId: string;
        title: string;
        description?: string;
        category?: 'assessment' | 'knowledge_check';
      },
    ) =>
      call<{ assessment: AssessmentShape; moduleItemId: string }>('/assessments', {
        method: 'POST',
        body,
        token,
      }),
    listForCourse: (token: string | null, courseId: string) =>
      call<{ assessments: AssessmentShape[] }>(`/assessments?courseId=${courseId}`, { token }),
    getByItemId: (token: string | null, itemId: string) =>
      call<{ assessment: AssessmentShape }>(`/assessments/by-item/${itemId}`, { token }),
    getTree: (token: string | null, id: string) =>
      call<AssessmentTreeShape>(`/assessments/${id}`, { token }),
    update: (
      token: string | null,
      id: string,
      body: Partial<{
        title: string;
        description: string | null;
        timeLimitSeconds: number | null;
        attemptsAllowed: number | null;
        scoringPolicy: 'highest' | 'latest' | 'average';
        availableFrom: number | null;
        availableUntil: number | null;
        autoRelease: boolean;
      }>,
    ) =>
      call<{ assessment: AssessmentShape }>(`/assessments/${id}`, {
        method: 'PATCH',
        body,
        token,
      }),
    addQuestion: (
      token: string | null,
      assessmentId: string,
      body: {
        type: QuestionType;
        promptHtml: string;
        points?: number;
        payloadJson?: string;
        modelAnswer?: string;
      },
    ) =>
      call<{ question: QuestionShape }>(`/assessments/${assessmentId}/questions`, {
        method: 'POST',
        body,
        token,
      }),
    updateQuestion: (
      token: string | null,
      questionId: string,
      body: Partial<{
        promptHtml: string;
        points: number;
        payloadJson: string | null;
        modelAnswer: string | null;
      }>,
    ) =>
      call<{ question: QuestionShape }>(`/assessments/questions/${questionId}`, {
        method: 'PATCH',
        body,
        token,
      }),
    deleteQuestion: (token: string | null, questionId: string) =>
      call<{ ok: true }>(`/assessments/questions/${questionId}`, { method: 'DELETE', token }),
    addOption: (
      token: string | null,
      questionId: string,
      body: {
        label: string;
        matchValue?: string | null;
        isCorrect?: boolean;
        pointsOverride?: number | null;
      },
    ) =>
      call<{ option: OptionShape }>(`/assessments/questions/${questionId}/options`, {
        method: 'POST',
        body,
        token,
      }),
    updateOption: (
      token: string | null,
      optionId: string,
      body: Partial<{
        label: string;
        matchValue: string | null;
        isCorrect: boolean;
        pointsOverride: number | null;
      }>,
    ) =>
      call<{ option: OptionShape }>(`/assessments/options/${optionId}`, {
        method: 'PATCH',
        body,
        token,
      }),
    deleteOption: (token: string | null, optionId: string) =>
      call<{ ok: true }>(`/assessments/options/${optionId}`, { method: 'DELETE', token }),
    startAttempt: (token: string | null, assessmentId: string) =>
      call<{ attempt: AttemptShape }>(`/assessments/${assessmentId}/attempts/start`, {
        method: 'POST',
        token,
      }),
    autosaveResponse: (
      token: string | null,
      attemptId: string,
      body: { questionId: string; responseJson: string },
    ) =>
      call<{ ok: true }>(`/assessments/attempts/${attemptId}/autosave`, {
        method: 'POST',
        body,
        token,
      }),
    /**
     * Load persisted draft responses for an in-progress attempt. Owner-only.
     * Used by the AssessmentTaker to hydrate prior answers when a returning
     * student resumes a tab they previously closed.
     */
    listResponses: (token: string | null, attemptId: string) =>
      call<{ responses: Array<{ questionId: string; responseJson: string }> }>(
        `/assessments/attempts/${attemptId}/responses`,
        { token },
      ),
    submitAttempt: (token: string | null, attemptId: string) =>
      call<SubmitResultShape>(`/assessments/attempts/${attemptId}/submit`, {
        method: 'POST',
        token,
      }),
    releaseAttempt: (token: string | null, attemptId: string) =>
      call<{ ok: true }>(`/assessments/attempts/${attemptId}/release`, {
        method: 'POST',
        token,
      }),
    gradeOverride: (
      token: string | null,
      attemptId: string,
      body: { questionId: string; awardedPoints: number; feedbackHtml?: string | null },
    ) =>
      call<{ ok: true }>(`/assessments/attempts/${attemptId}/grade-override`, {
        method: 'POST',
        body,
        token,
      }),
    fromQuizConversion: (
      token: string | null,
      body: {
        sourceModuleItemId: string;
        title: string;
        questions: Array<{
          prompt: string;
          options: string[];
          correctIndex: number;
          explanation?: string;
        }>;
      },
    ) =>
      call<{ assessmentId: string; moduleItemId: string }>('/assessments/from-quiz-conversion', {
        method: 'POST',
        body,
        token,
      }),
  },
  conversations: {
    list: (token: string | null) =>
      call<{ conversations: ConversationSummaryShape[] }>('/conversations', { token }),
    create: (
      token: string | null,
      body: {
        kind: 'direct' | 'group' | 'course_room';
        participantProfileIds: string[];
        courseId?: string | null;
        orgId?: string | null;
        title?: string | null;
      },
    ) =>
      call<{ id: string; alreadyExisted: boolean }>('/conversations', {
        method: 'POST',
        body,
        token,
      }),
    listMessages: (token: string | null, conversationId: string) =>
      call<{ messages: StoredMessageShape[] }>(`/conversations/${conversationId}/messages`, {
        token,
      }),
    sendMessage: (
      token: string | null,
      conversationId: string,
      body: { body: string; replyToMessageId?: string | null },
    ) =>
      call<{ message: StoredMessageShape }>(`/conversations/${conversationId}/messages`, {
        method: 'POST',
        body,
        token,
      }),
    markRead: (token: string | null, conversationId: string) =>
      call<{ ok: true }>(`/conversations/${conversationId}/read`, { method: 'POST', token }),
    startWithEmail: (token: string | null, body: { email: string }) =>
      call<{ id: string; alreadyExisted: boolean }>('/conversations/start-with-email', {
        method: 'POST',
        body,
        token,
      }),
  },
  notifications: {
    list: (token: string | null) =>
      call<{ notifications: NotificationShape[] }>('/notifications', { token }),
    unreadCount: (token: string | null) =>
      call<{ unread: number }>('/notifications/unread-count', { token }),
    markRead: (token: string | null, id: string) =>
      call<{ ok: true }>(`/notifications/${id}/read`, { method: 'POST', token }),
    markAllRead: (token: string | null) =>
      call<{ ok: true }>('/notifications/read-all', { method: 'POST', token }),
  },
  realtime: {
    issueWsTicket: (token: string | null) =>
      call<{ ticket: string; ttlSeconds: number }>('/auth/ws-ticket', {
        method: 'POST',
        token,
      }),
  },
  discussions: {
    listThreads: (token: string | null, courseId: string) =>
      call<{ threads: ThreadSummaryShape[] }>(
        `/discussions?courseId=${encodeURIComponent(courseId)}`,
        { token },
      ),
    createThread: (
      token: string | null,
      body: { courseId: string; moduleId?: string | null; title: string; bodyHtml: string },
    ) => call<{ id: string }>('/discussions', { method: 'POST', body, token }),
    listPosts: (token: string | null, threadId: string) =>
      call<{ posts: DiscussionPostShape[] }>(`/discussions/${threadId}/posts`, { token }),
    createPost: (
      token: string | null,
      threadId: string,
      body: { bodyHtml: string; parentPostId?: string | null },
    ) =>
      call<{ post: DiscussionPostShape }>(`/discussions/${threadId}/posts`, {
        method: 'POST',
        body,
        token,
      }),
  },
  announcements: {
    listForCourse: (token: string | null, courseId: string) =>
      call<{ announcements: AnnouncementSummaryShape[] }>(
        `/announcements?courseId=${encodeURIComponent(courseId)}`,
        { token },
      ),
    create: (
      token: string | null,
      body: {
        scope: 'course' | 'org' | 'cross_course' | 'platform';
        title: string;
        bodyHtml: string;
        courseId?: string | null;
        orgId?: string | null;
        crossCourseIds?: string[];
        scheduledFor?: number | null;
        sendEmail?: boolean;
      },
    ) =>
      call<{ id: string; scheduled: boolean }>('/announcements', {
        method: 'POST',
        body,
        token,
      }),
  },
  risks: {
    listForCourse: (token: string | null, courseId: string) =>
      call<{ risks: StudentRiskShape[] }>(`/risks?courseId=${encodeURIComponent(courseId)}`, {
        token,
      }),
  },
  ai: {
    beautifyAnnouncement: (token: string | null, body: { bodyHtml: string }) =>
      call<{ bodyHtml: string; simulated?: boolean }>('/ai/beautify-announcement', {
        method: 'POST',
        body,
        token,
      }),
    convert: (
      token: string | null,
      body: {
        sourceText: string;
        targetType: 'quiz' | 'flashcards' | 'slide_deck' | 'lesson_plan' | 'rubric';
        targetCount?: number;
      },
    ) =>
      call<{ targetType: string; result: string; simulated?: boolean }>('/ai/convert', {
        method: 'POST',
        body,
        token,
      }),
    sparkCourse: (
      token: string | null,
      body: {
        brief: string;
        audience?: string;
        moduleCount?: number;
        lessonsPerModule?: number;
      },
    ) =>
      call<{ simulated: boolean; outline: SparkOutlineShape }>('/ai/spark-course', {
        method: 'POST',
        body,
        token,
      }),
    sparkLessonContent: (token: string | null, body: { moduleItemId: string; audience?: string }) =>
      call<{
        simulated: boolean;
        blocks: Array<{ type: 'heading' | 'text'; payloadJson: string }>;
      }>('/ai/spark-lesson-content', { method: 'POST', body, token }),
    /**
     * Phase-7 onboarding helper. Drafts a starter Vibe Space (tagline /
     * about / accent / hero emoji) for the caller's primary org. The
     * Settings → Vibe Space tab pre-fills its form with the result.
     */
    sparkVibeSpace: (token: string | null, body: { brief?: string; audience?: string }) =>
      call<{
        simulated: boolean;
        draft: {
          tagline: string | null;
          aboutHtml: string | null;
          accentColor: string | null;
          heroEmoji: string | null;
        };
      }>('/ai/spark-vibe-space', { method: 'POST', body, token }),
    /**
     * Tutoring Center turn. `history` is the prior transcript (oldest first),
     * capped server-side at 20 entries. The API adds the new `question` itself
     * — don't include it in `history`.
     *
     * When `courseId` is provided, the server retrieves up to 5 relevant
     * lesson snippets from that course's content (via D1 FTS5) and
     * injects them into the prompt. The retrieved snippets are echoed
     * back as `sources` so the SPA can render citations.
     */
    tutor: (
      token: string | null,
      body: {
        question: string;
        courseId?: string;
        history?: Array<{ role: 'user' | 'assistant'; content: string }>;
      },
    ) =>
      call<{
        reply: string;
        simulated?: boolean;
        model?: string;
        tokensUsed?: number;
        sources?: TutorSourceShape[];
      }>('/ai/tutor', { method: 'POST', body, token }),
  },
  studyRoom: {
    /** Start or resume the open Study Room session for (course, caller). */
    start: (token: string | null, body: { courseId: string; focusModuleItemId?: string | null }) =>
      call<{ session: StudySessionShape; messages: StudyMessageShape[] }>('/ai/study-session', {
        method: 'POST',
        body,
        token,
      }),
    /** Load transcript + state for resume. */
    load: (token: string | null, sessionId: string) =>
      call<{ session: StudySessionShape; messages: StudyMessageShape[] }>(
        `/ai/study-session/${sessionId}`,
        { token },
      ),
    /** One Q&A turn. */
    turn: (
      token: string | null,
      sessionId: string,
      body: { question: string; mode: StudyMode; focusModuleItemId?: string | null },
    ) =>
      call<{
        reply: string;
        simulated?: boolean;
        sources?: TutorSourceShape[];
        session: StudySessionShape;
      }>(`/ai/study-session/${sessionId}/turn`, { method: 'POST', body, token }),
    /** Finalize. Idempotent. Commits active_ms to the gradebook. */
    end: (token: string | null, sessionId: string) =>
      call<{ ok: true; activeMs: number; alreadyEnded: boolean }>(
        `/ai/study-session/${sessionId}/end`,
        { method: 'POST', token },
      ),
  },
  vibeSpace: {
    /** Public — no auth. 404 when org doesn't exist or vibeSpaceVisible=false. */
    getPublic: (orgSlug: string) =>
      call<VibeSpaceResponse>(`/vibe-space/${encodeURIComponent(orgSlug)}`),
    /** Auth — admin or instructor in this org. */
    update: (
      token: string | null,
      orgId: string,
      body: {
        tagline?: string | null;
        aboutHtml?: string | null;
        accentColor?: string | null;
        heroEmoji?: string | null;
        vibeSpaceVisible?: boolean;
      },
    ) =>
      call<{ vibeSpace: VibeSpaceSettings }>(`/orgs/${orgId}/vibe-space`, {
        method: 'PATCH',
        body,
        token,
      }),
  },
  uploads: {
    /**
     * Upload a logo or banner image for an org. Multipart; the file goes
     * to R2 via the Worker. Returns the new R2 key, which the server
     * has already persisted on the org row. The SPA should refetch the
     * org via `api.orgs.me` to pick up the new key.
     */
    uploadOrgImage: async (
      token: string | null,
      orgId: string,
      kind: 'logo' | 'banner',
      file: File,
    ): Promise<{ key: string; kind: string }> => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(
        `${BASE}/uploads/org/${orgId}/image?kind=${encodeURIComponent(kind)}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        },
      );
      const payload = (await res.json().catch(() => null)) as {
        key?: string;
        kind?: string;
        error?: string;
        message?: string;
      } | null;
      if (!res.ok || !payload?.key || !payload?.kind) {
        throw new ApiError(res.status, payload?.error ?? `http_${res.status}`, payload?.message);
      }
      return { key: payload.key, kind: payload.kind };
    },
    /** Public image-serve URL for an R2 key returned by uploadOrgImage. */
    objectUrl: (key: string) => `${BASE}/uploads/o/${key}`,
  },
  templates: {
    /** Public — list every published-as-template course across orgs. */
    list: () => call<{ templates: TemplateSummary[] }>('/templates'),
    /** Auth + instructor. Deep-clones the template into the caller's
     *  primary org and returns the new course's id + slug. */
    clone: (token: string | null, templateId: string) =>
      call<{ id: string; slug: string }>(`/templates/${templateId}/clone`, {
        method: 'POST',
        token,
      }),
  },
  agent: {
    listRules: (token: string | null) =>
      call<{ rules: AgentRuleShape[] }>('/agent/rules', { token }),
    createRule: (
      token: string | null,
      body: {
        triggerType: AgentTriggerType;
        actionType?: 'notify' | 'email' | 'both';
        courseId?: string | null;
        config?: Record<string, unknown>;
      },
    ) => call<{ id: string }>('/agent/rules', { method: 'POST', body, token }),
    deleteRule: (token: string | null, id: string) =>
      call<{ ok: true }>(`/agent/rules/${id}`, { method: 'DELETE', token }),
    listLogs: (token: string | null) => call<{ logs: AgentLogShape[] }>('/agent/logs', { token }),
  },
};

/* ─── Agent shapes ────────────────────────────────────────────────────── */

export type AgentTriggerType =
  | 'assignment_due_24h'
  | 'student_inactive_7d'
  | 'low_score'
  | 'new_discussion_post';

export type AgentRuleShape = {
  id: string;
  triggerType: AgentTriggerType;
  actionType: 'notify' | 'email' | 'both';
  courseId: string | null;
  enabled: boolean;
  lastFiredAt: number | null;
  createdAt: number;
};

export type AgentLogShape = {
  id: string;
  ruleId: string;
  triggerType: string;
  payload: unknown;
  firedAt: number;
};

/* ─── Spark course outline shape ───────────────────────────────────────── */

export type SparkOutlineShape = {
  course: { title: string; description: string; slug: string };
  modules: Array<{
    title: string;
    summary?: string;
    lessons: Array<{ title: string; summary?: string }>;
  }>;
};

/* ─── 4c / 5 / 6 shape types ───────────────────────────────────────────── */

export type ThreadSummaryShape = {
  id: string;
  courseId: string;
  moduleId: string | null;
  authorProfileId: string;
  title: string;
  bodyHtml: string;
  pinned: boolean;
  locked: boolean;
  postCount: number;
  lastPostAt: number | null;
  createdAt: number;
};

export type DiscussionPostShape = {
  id: string;
  threadId: string;
  authorProfileId: string;
  parentPostId: string | null;
  bodyHtml: string;
  createdAt: number;
};

export type AnnouncementSummaryShape = {
  id: string;
  scope: 'course' | 'org' | 'cross_course' | 'platform';
  title: string;
  bodyHtml: string;
  courseId: string | null;
  orgId: string | null;
  authorProfileId: string;
  scheduledFor: number | null;
  publishedAt: number | null;
  sendEmail: boolean;
  createdAt: number;
};

export type StudentRiskShape = {
  profileId: string;
  fullName: string;
  email: string;
  score: number;
  band: 'red' | 'amber' | 'green';
  signals: {
    daysSinceLastActivity: number | null;
    missedAssignments: number;
    averageScorePct: number | null;
    failingAttempts: number;
  };
};

/* ─── Realtime/messaging types (mirrored from api Worker payloads) ──────── */

export type ConversationSummaryShape = {
  id: string;
  kind: 'direct' | 'group' | 'course_room';
  title: string | null;
  courseId: string | null;
  orgId: string | null;
  lastMessageAt: number | null;
  createdAt: number;
  unread: boolean;
  memberProfileIds: string[];
};

export type StoredMessageShape = {
  id: string;
  conversationId: string;
  senderProfileId: string;
  body: string;
  replyToMessageId: string | null;
  createdAt: number;
};

export type NotificationShape = {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  targetUrl: string | null;
  payload: unknown;
  readAt: number | null;
  createdAt: number;
};

/** Base URL for WebSocket connections — set at build time. */
export const WS_BASE = (import.meta.env.VITE_WS_BASE_URL ?? '').replace(/\/$/, '');
